import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmt, VAULT_PATH } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

const TARGET = 20000;

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(`${VAULT_PATH}/login`, { replace: true });
        return;
      }
    } finally {
      setRefreshing(false);
      setChecking(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout call failed — redirecting anyway", e);
    }
    navigate(`${VAULT_PATH}/login`, { replace: true });
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (checking) {
    return (
      <div className="bg-white text-[#0a0a0a] min-h-screen">
        <Header />
        <div className="mx-auto max-w-[1400px] px-6 py-20 text-center font-mono text-xs uppercase tracking-widest text-[#52525b]">
          Loading…
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const estAffiliateRevenue = stats.affiliate_clicks * 0.02 * 80 * 0.05;
  const estTotalRevenue = stats.sponsor_revenue_usd + estAffiliateRevenue;
  const monthProgressPct = Math.min(100, (estTotalRevenue / TARGET) * 100);

  return (
    <div data-testid="admin-page" className="bg-white text-[#0a0a0a]">
      <Seo title="Vault — Universtrend" description="Owner-only dashboard" />
      <Header />

      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline text-[#52525b]">The Vault · Revenue Control Room</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                Your money,<br /><span className="text-[#ff3b30]">in real time.</span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={`${VAULT_PATH}/press`}
                data-testid="admin-press-link"
                className="hard-shadow border border-black bg-white text-black px-5 py-2.5 overline"
              >
                Press Kit →
              </Link>
              <Link
                to={`${VAULT_PATH}/newsletter`}
                data-testid="admin-newsletter-link"
                className="hard-shadow border border-black bg-white text-black px-5 py-2.5 overline"
              >
                Newsletter →
              </Link>
              <Link
                to={`${VAULT_PATH}/social`}
                data-testid="admin-social-link"
                className="hard-shadow border border-black bg-[#ff3b30] text-white px-5 py-2.5 overline"
              >
                Social Studio →
              </Link>
              <button
                data-testid="admin-refresh"
                onClick={load}
                className="hard-shadow border border-black bg-black text-white px-5 py-2.5 overline"
              >
                {refreshing ? "…" : "Refresh"}
              </button>
              <button
                data-testid="admin-logout"
                onClick={logout}
                className="border border-black bg-white text-black px-5 py-2.5 overline hover:bg-[#f4f4f5]"
              >
                Lock →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-black">
            <div className="border-r border-b border-black p-6">
              <div className="overline text-[#52525b]">Sponsor revenue (paid)</div>
              <div className="mt-3 font-display font-extrabold tracking-tighter text-5xl lg:text-6xl">
                ${stats.sponsor_revenue_usd.toFixed(2)}
              </div>
              <div className="mt-2 font-mono text-xs text-[#52525b]">{stats.paid_sponsors} paid sponsorship{stats.paid_sponsors === 1 ? "" : "s"}</div>
            </div>
            <div className="border-r border-b border-black p-6">
              <div className="overline text-[#52525b]">Est. affiliate revenue</div>
              <div className="mt-3 font-display font-extrabold tracking-tighter text-5xl lg:text-6xl">
                ${estAffiliateRevenue.toFixed(2)}
              </div>
              <div className="mt-2 font-mono text-xs text-[#52525b]">{stats.affiliate_clicks} clicks · 2% conv. × $80 AOV × 5%</div>
            </div>
            <div className="border-r border-b border-black p-6">
              <div className="overline text-[#52525b]">Newsletter subscribers</div>
              <div className="mt-3 font-display font-extrabold tracking-tighter text-5xl lg:text-6xl">
                {stats.subscribers}
              </div>
              <div className="mt-2 font-mono text-xs text-[#52525b]">~${(stats.subscribers * 3).toFixed(0)} lifetime value @ $3/sub</div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-3">
              <div className="overline text-[#52525b]">Progress toward $20k / month</div>
              <div className="font-mono text-xs">${estTotalRevenue.toFixed(2)} / ${TARGET.toLocaleString()}</div>
            </div>
            <div className="h-8 border border-black relative bg-white" data-testid="progress-bar">
              <div className="h-full bg-[#ff3b30]" style={{ width: `${monthProgressPct}%` }} />
              <div className="absolute inset-0 flex items-center justify-center font-mono text-xs">
                {monthProgressPct.toFixed(2)}%
              </div>
            </div>
          </div>

          {stats.active_sponsor && (
            <div className="mt-12 border border-black p-6 bg-[#f4f4f5]">
              <div className="overline text-[#52525b]">Active sponsor</div>
              <div className="mt-2 font-display font-extrabold text-2xl tracking-tight">{stats.active_sponsor.brand_name}</div>
              <div className="font-mono text-xs text-[#52525b] mt-1">
                &ldquo;{stats.active_sponsor.tagline}&rdquo; · until {stats.active_sponsor.active_until.slice(0, 10)}
              </div>
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 border-t border-l border-black">
            <div className="border-r border-b border-black p-6">
              <div className="overline text-[#52525b] mb-4">Affiliate breakdown</div>
              {stats.affiliate_breakdown.length === 0 ? (
                <div className="font-mono text-xs text-[#52525b]">No clicks yet.</div>
              ) : (
                <ul className="divide-y divide-black/15">
                  {stats.affiliate_breakdown.map((a) => (
                    <li key={a.affiliate_id} className="flex justify-between py-2 font-mono text-sm" data-testid={`admin-aff-${a.affiliate_id}`}>
                      <span>{a.affiliate_id}</span>
                      <span>{a.clicks} clicks · ${(a.clicks * 0.02 * 80 * 0.05).toFixed(2)} est.</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-r border-b border-black p-6">
              <div className="overline text-[#52525b] mb-4">Playbook to hit $20k</div>
              <ol className="space-y-3 text-sm">
                <li><span className="font-mono text-[#52525b] mr-2">01</span>Activate AdSense — set <code className="font-mono text-xs bg-[#f4f4f5] px-1">REACT_APP_ADSENSE_CLIENT</code></li>
                <li><span className="font-mono text-[#52525b] mr-2">02</span>Swap Amazon Associates tags into <code className="font-mono text-xs bg-[#f4f4f5] px-1">/api/affiliates</code></li>
                <li><span className="font-mono text-[#52525b] mr-2">03</span>Post on Reddit r/dataisbeautiful + X — first traffic surge</li>
                <li><span className="font-mono text-[#52525b] mr-2">04</span>Submit sitemap <code className="font-mono text-xs bg-[#f4f4f5] px-1">/api/sitemap.xml</code> to Google Search Console</li>
                <li><span className="font-mono text-[#52525b] mr-2">05</span>Sell 4 sponsors / month at $349 = $1,400 recurring</li>
                <li><span className="font-mono text-[#52525b] mr-2">06</span>5,000 sub. newsletter × $3/sub = $15k/yr passive</li>
              </ol>
            </div>
          </div>

          <div className="mt-4 font-mono text-xs text-[#52525b]">
            As of {stats.as_of.replace("T", " ").slice(0, 19)} UTC · auto-refreshes every 15s
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
